import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { ChurchIcon, RouteIcon, DollarSignIcon, UsersIcon, MedalIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const ccuEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const ccuStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}];
interface CcuProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const ccuPrograms: CcuProgram[] = [
// Associate
{
  shortTitle: 'A.S. - Project Management',
  longTitle: 'Associate of Science (A.S.) in Project Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '1531'
},
// Bachelor's
{
  shortTitle: 'B.A. - Biblical Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Biblical Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'religious-studies',
  mProgram: '631'
}, {
  shortTitle: 'B.A. - Christian Ministry',
  longTitle: 'Bachelor of Arts (B.A.) in Christian Ministry',
  degreeLevel: 'bachelors',
  areaOfStudy: 'religious-studies',
  mProgram: '643'
}, {
  shortTitle: 'B.A. - Communication Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Communication Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: '649'
}, {
  shortTitle: 'B.A. - Creative Writing',
  longTitle: 'Bachelor of Arts (B.A.) in Creative Writing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: '657'
}, {
  shortTitle: 'B.A. - Early Childhood Education (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '669'
}, {
  shortTitle: 'B.A. - Early Childhood Education (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '670'
}, {
  shortTitle: 'B.A. - Elementary Education (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Elementary Education (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '676'
}, {
  shortTitle: 'B.A. - Elementary Education (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Elementary Education (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '677'
}, {
  shortTitle: 'B.A. - Special Education, K-12 Generalist (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Special Education, K-12 Generalist (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '710'
}, {
  shortTitle: 'B.A. - Special Education, K-12 Generalist (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Special Education, K-12 Generalist (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '711'
}, {
  shortTitle: 'B.S. - Accountancy',
  longTitle: 'Bachelor of Science (B.S.) in Accountancy',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '620'
}, {
  shortTitle: 'B.S. - Applied Psychology',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '704'
}, {
  shortTitle: 'B.S. - Applied Psychology - Biblical Studies',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Biblical Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '705'
}, {
  shortTitle: 'B.S. - Applied Psychology - Clinical Counseling',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Clinical Counseling',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '706'
}, {
  shortTitle: 'B.S. - Applied Psychology - Criminal Justice',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '707'
}, {
  shortTitle: 'B.S. - Business Administration',
  longTitle: 'Bachelor of Science (B.S.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '634'
}, {
  shortTitle: 'B.S. - Business - Entrepreneurship',
  longTitle: 'Bachelor of Science (B.S.) in Business - Entrepreneurship',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '635'
}, {
  shortTitle: 'B.S. - Business - International Business',
  longTitle: 'Bachelor of Science (B.S.) in Business - International Business',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '636'
}, {
  shortTitle: 'B.S. - Computer Information Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '651'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '652'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Database Management',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Database Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '653'
}, {
  shortTitle: 'B.S. - Computer Information Technology, Data Engineering',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology, Data Engineering',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '719'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Networking',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Networking',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '654'
}, {
  shortTitle: 'B.S. - Computer Science',
  longTitle: 'Bachelor of Science (B.S.) in Computer Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '656'
}, {
  shortTitle: 'B.S. - Criminal Justice',
  longTitle: 'Bachelor of Science (B.S.) in Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '659'
}, {
  shortTitle: 'B.S. - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1767'
}, {
  shortTitle: 'B.S. - Data Analytics',
  longTitle: 'Bachelor of Science (B.S.) in Data Analytics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '668'
}, {
  shortTitle: 'B.S. - Economics',
  longTitle: 'Bachelor of Science (B.S.) in Economics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '671'
}, {
  shortTitle: 'B.S. - Health Care Administration',
  longTitle: 'Bachelor of Science (B.S.) in Health Care Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: '680'
}, {
  shortTitle: 'B.S. - Human Resource Management',
  longTitle: 'Bachelor of Science (B.S.) in Human Resource Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '682'
}, {
  shortTitle: 'B.S. - Information Systems Management',
  longTitle: 'Bachelor of Science (B.S.) in Information Systems Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '684'
}, {
  shortTitle: 'B.S. - Information Systems Management - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Information Systems Management - Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '686'
}, {
  shortTitle: 'B.S. - Logistics and Supply Chain Management',
  longTitle: 'Bachelor of Science (B.S.) in Logistics and Supply Chain Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '688'
}, {
  shortTitle: 'B.S. - Marketing and Sales',
  longTitle: 'Bachelor of Science (B.S.) in Marketing and Sales',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '690'
}, {
  shortTitle: 'B.S. - Nonprofit Management',
  longTitle: 'Bachelor of Science (B.S.) in Nonprofit Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '692'
}, {
  shortTitle: 'B.S. - Organizational Management in Project Management',
  longTitle: 'Bachelor of Science (B.S.) in Organizational Management in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '699'
},
// Master's
{
  shortTitle: 'M.A. - Biblical Studies',
  longTitle: 'Master of Arts (M.A.) in Biblical Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '632'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Marriage & Family Therapy',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Marriage & Family Therapy',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '646'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Online Curriculum',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Online Curriculum',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '645'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - On Site Curriculum',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - On Site Curriculum',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '644'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Substance Use Disorders',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Substance Use Disorders',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '647'
}, {
  shortTitle: 'M.A. - Counseling Dual - Mental Health & School',
  longTitle: 'Master of Arts (M.A.) in Counseling Dual - Mental Health & School',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '723'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '665'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Alternative Licensing',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Alternative Licensing',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '662'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Biblical Studies',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Biblical Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '663'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Culturally and Linguistically Diverse',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Culturally and Linguistically Diverse',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '664'
}, {
  shortTitle: 'M.A. - Executive Leadership in Christian Ministry',
  longTitle: 'Master of Arts (M.A.) in Executive Leadership in Christian Ministry',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '678'
}, {
  shortTitle: 'M.A. - School Counseling',
  longTitle: 'Master of Arts (M.A.) in School Counseling',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '709'
}, {
  shortTitle: 'M.A. - Theological Studies',
  longTitle: 'Master of Arts (M.A.) in Theological Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '716'
}, {
  shortTitle: 'M.B.A. - Business Administration - Advanced Accounting',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Advanced Accounting',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '637'
}, {
  shortTitle: 'M.B.A. - Business Administration - Cyber Security',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Cyber Security',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '638'
}, {
  shortTitle: 'M.B.A. - Business Administration, Economics',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration, Economics',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '718'
}, {
  shortTitle: 'M.B.A. - Business Administration - Enterprise Agility',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Enterprise Agility',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '642'
}, {
  shortTitle: 'M.B.A. - Business Administration - Health Care Administration',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Health Care Administration',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '639'
}, {
  shortTitle: 'M.B.A. - Business Administration - Leadership',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '640'
}, {
  shortTitle: 'M.B.A. - Business Administration - Project Management',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Project Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '641'
}, {
  shortTitle: 'M.Ed. - Educational Leadership (Principal Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Educational Leadership (Principal Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '673'
}, {
  shortTitle: 'M.Ed. - Educational Leadership (without Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Educational Leadership (without Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '674'
}, {
  shortTitle: 'M.Ed. - Education - Teaching English to Speakers of Other Languages',
  longTitle: 'Master of Education (M.Ed.) in Education - Teaching English to Speakers of Other Languages',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '675'
}, {
  shortTitle: 'M.Ed. - Special Education - Emphasis in Alternative Licensing',
  longTitle: 'Master of Education (M.Ed.) in Special Education - Emphasis in Alternative Licensing',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '713'
}, {
  shortTitle: 'M.Ed. - Special Education - Endorsement in Special Education Generalist',
  longTitle: 'Master of Education (M.Ed.) in Special Education - Endorsement in Special Education Generalist',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '714'
}, {
  shortTitle: 'M.Ed. - Special Education (Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Special Education (Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '715'
}, {
  shortTitle: 'M.Ed. - Special Education (without Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Special Education (without Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '712'
}, {
  shortTitle: 'M.S. - Accounting',
  longTitle: 'Master of Science (M.S.) in Accounting',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '621'
}, {
  shortTitle: 'M.S. - Criminal Justice',
  longTitle: 'Master of Science (M.S.) in Criminal Justice',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: '660'
}, {
  shortTitle: 'M.S. - Criminal Justice - Campus, Event, and Organizational Safety',
  longTitle: 'Master of Science (M.S.) in Criminal Justice - Campus, Event, and Organizational Safety',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: '661'
}, {
  shortTitle: 'M.S. - Cyber Security',
  longTitle: 'Master of Science (M.S.) in Cyber Security',
  degreeLevel: 'masters',
  areaOfStudy: 'information-technology',
  mProgram: '666'
}, {
  shortTitle: 'M.S. - Human Resource Management',
  longTitle: 'Master of Science (M.S.) in Human Resource Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '683'
}, {
  shortTitle: 'M.S. - Master in Organizational Leadership',
  longTitle: 'Master of Science (M.S.) in Master in Organizational Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '701'
}, {
  shortTitle: 'M.S. - Psychology',
  longTitle: 'Master of Science (M.S.) in Psychology',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '703'
}];
export function CCULandingPage() {
  usePageMeta('Colorado Christian University | Online Degree Programs', 'Explore flexible online degree programs at Colorado Christian University. Accredited Christ-centered programs designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'health-care': 'Health Care',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    psychology: 'Psychology & Counseling',
    'religious-studies': 'Religious Studies & Ministry'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    ccuPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return ccuPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = ccuPrograms.find(p => p.shortTitle === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/uv7w6tl/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Colorado Christian University',
            logo: 'https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp',
            primaryColor: '#0F3057',
            accentColor: '#D4A943',
            backUrl: '/colleges/colorado-christian-university',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1006" data-mp="3p3gmw358jwcw">
          <div data-id="element-1007" data-mp="3p4i12ghjsyyu">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1008" data-mp="3u0z0w7y411rr">
              Degree Level
            </label>
            <div className="relative" data-id="element-1009" data-mp="3ahjeaytmysqg">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800" data-id="element-1010" data-mp="35h5e22ya0n2z">
                <option value="" data-id="element-1011" data-mp="3tdj7gcyco2bm">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-1012" data-mp="3ofq37pglxidz">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1013" data-mp="6xx0m3wemcn4">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1014" data-mp="21zns7kcnof3" />
              </div>
            </div>
          </div>

          <div data-id="element-1015" data-mp="b0bmkwvddsim">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1016" data-mp="b0tr0gs6rd1m">
              Area of Study
            </label>
            <div className="relative" data-id="element-1017" data-mp="39re4bz066kqr">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1018" data-mp="39sfs942ab1pw">
                <option value="" data-id="element-1019" data-mp="3sgt1hicqippv">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-1020" data-mp="5dg0tlx4tb3b">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1021" data-mp="234qy7l6onvc6">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1022" data-mp="276civspgldwx" />
              </div>
            </div>
          </div>

          <div data-id="element-1023" data-mp="1uua55cpec8wb">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1024" data-mp="1uts0psskyodb">
              Program
            </label>
            <div className="relative" data-id="element-1025" data-mp="1pt47r1ksjxgg">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1026" data-mp="1lueowtgzhnpf">
                <option value="" data-id="element-1027" data-mp="24kmmyx5ttnyr">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.shortTitle} value={prog.shortTitle} data-id="element-1028" data-mp="t57r5wpct258">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1029" data-mp="2t5w6lupz5lqb">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1030" data-mp="2x6g3qyajspcg" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-1031" data-mp="2jbckqfgs3y55">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1032" data-mp="3bzrzd0w00efy">
          <div data-id="element-1033" data-mp="3gxa0w67orrdx">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1034" data-mp="3gvqj2b6dt8a1">
              Selected Program
            </label>
            <div className="relative" data-id="element-1035" data-mp="376p5ernb72z8">
              <select id="step1-program" value={selectedProgram} onChange={e => setSelectedProgram(e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1036" data-mp="375nqud733ycr">
                {availablePrograms.map(prog => <option key={prog.shortTitle} value={prog.shortTitle} data-id="element-1037" data-mp="1o6zmflpxjhv8">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1038" data-mp="1x0cphghpjzv1">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1039" data-mp="1syr394jaa986" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1040" data-mp="158n3x9d4ovmz" />

          <div data-id="element-1041" data-mp="10abqkonmk300">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1042" data-mp="10all8pjm6yc1">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1043" data-mp="19b06jowkwdfj">
              <option value="" data-id="element-1044" data-mp="2kq6z7p6qd8b2">-- Please Select --</option>
              {ccuEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1045" data-mp="2ksjhy4kbbmp6">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1046" data-mp="2v9les0mosf7c">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1047" data-mp="2lcyrz6asve2o">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1048" data-mp="2ld8mn76si9ep">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1049" data-mp="3e1nw7e75jzin">
              <option value="" data-id="element-1050" data-mp="399372pz6yalg">-- Please Select --</option>
              {ccuStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1051" data-mp="34bbdlriwegf5">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1052" data-mp="um6a3bhr2of">{errors.startDate}</p>}
          </div>

          <div data-id="element-1053" data-mp="86ipmvdwg739">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1054" data-mp="8727yd7goxht">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1055" data-mp="i4q14ypf5wxv">
              <option value="" data-id="element-1056" data-mp="n1gh7m184sja">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1057" data-mp="r4lg7em3ic36">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1058" data-mp="10uy0d5q0038s">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1059" data-mp="10xahxhrugblb">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1060" data-mp="15szjfqnfc2k9">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1061" data-mp="1euz18l69jao9" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1062" data-mp="3bvlcncmo0ckz">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1063" data-mp="3ul1rz630e9lg">
          <div data-id="element-1064" data-mp="3uk9yc0sz2lxt">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1065" data-mp="3unxpitay2w9d">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1066" data-mp="7g3oyxuuo7qh" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1067" data-mp="1v413mteu89jw">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1068" data-mp="1z4tdnovjaih8">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1069" data-mp="1lderj96by15y" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1070" data-mp="1q9wwoa5ko1it">
                <div data-id="element-1071" data-mp="1be7hovyt0onl">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1072" data-mp="1bdnylbxf3u7z">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#D4A943] focus:border-[#D4A943] bg-gray-50" placeholder="City" data-id="element-1073" data-mp="12fc7z9wjwwfj" />
                </div>
                <div data-id="element-1074" data-mp="31q3k6aciu0xc">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1075" data-mp="31pc282buk1pj">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#D4A943] focus:border-[#D4A943] bg-gray-50" placeholder="State" data-id="element-1076" data-mp="wrdnzy1y5u3y" />
                </div>
              </div>
              <div data-id="element-1077" data-mp="isdx0jqyp34m">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1078" data-mp="dzumxwrhgq4k">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`} placeholder="123 Main St" data-id="element-1079" data-mp="8y5accom44ed" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1080" data-mp="2bmncdyv5xu81">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1081" data-mp="2foif97sqsfnx">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1082" data-mp="2fmnr7km6c7fm">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1083" data-mp="1rywdqdowdocr" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1084" data-mp="1wyiqyn4q74lk">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1085" data-mp="196v8mtlj51uw">
          <div className="grid grid-cols-2 gap-3" data-id="element-1086" data-mp="2cjlqk81yhnyn">
            <div data-id="element-1087" data-mp="2ci0ycp346h3y">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1088" data-mp="2chhf951q9moc">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1089" data-mp="2lam494lnctbd" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1090" data-mp="1tkzr0xm384wu">{errors.firstName}</p>}
            </div>
            <div data-id="element-1091" data-mp="1onhgx3wc3jn9">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1092" data-mp="1onzkkllbt057">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1093" data-mp="1xkrs6p95j8s6" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1094" data-mp="391880jombrfk">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1095" data-mp="3hkv5jusuxwo7">
            <div data-id="element-1096" data-mp="3ll5970t6gazw">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1097" data-mp="3llnfyrdgu7m2">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1098" data-mp="3cimamswfckjg" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1099" data-mp="91dtkb9itops">{errors.email}</p>}
            </div>
            <div data-id="element-1100" data-mp="3qi5hlidi1pu6">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1101" data-mp="3qinl902hr6c4">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1102" data-mp="n26q11jdutvj" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1103" data-mp="2nxmgzixbmtxl">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#D4A943] focus:ring-[#D4A943] cursor-pointer flex-shrink-0" data-id="element-1104" data-mp="2j4a1pzej7zhi">
            By submitting this form, I agree to be called or texted at the
            number listed above by or on behalf of Colorado Christian University
            (CCU) using an automatic or AI telephone system. All communication
            will be informational and commercial regarding educational services.
            Consent is not required as a condition of enrollment. CCU may also
            contact me via email.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1105" data-mp="1hg9493e9y7fj">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1106" data-mp="1hfg5aolk4m7y">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1107" data-mp="1uxd9nsvue9e" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1108" data-mp="5wsg4wqgozpa">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1109" data-mp="pndyu8btwodg" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1110" data-mp="2rg22ujwarfua">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1111" data-mp="2vgci6n5zbilt">
        <img src="https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp" alt="Colorado Christian University Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-1112" data-mp="36mg51wj2nqzl" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0F3057] to-[#1A4A7A] relative overflow-hidden" data-id="element-1113" data-mp="3fll4d52gjoct">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-1114" data-mp="3kk4p525beu3l" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-1115" data-mp="297byjuo9gbp4">
          {/* Headline */}
          <div className="text-white max-w-xl text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-1116" data-mp="21n1cxtn05kgr">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-1117" data-mp="1wmvk4a4zxrx3">
              Colorado Christian University
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-1118" data-mp="1mrbozpy9ax9e">
              {selectedProgram && selectedProgram !== 'undecided' ? (() => {
              const currentProg = ccuPrograms.find(p => p.shortTitle === selectedProgram);
              return currentProg?.longTitle || selectedProgram;
            })() : <>
                  Christ-Centered
                  <br data-id="element-1119" data-mp="1413ozgpusmxf" />
                  Online Degree Programs
                </>}
            </h1>
          </div>

          {/* Supporting copy. Row placement puts it under the form on mobile
              and back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed row-start-3 lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-1120" data-mp="v1qh2shdo6et">
              Colorado Christian University offers accredited online degree programs taught with a Christian worldview. With a flexible online format, dedicated support staff, and affordable tuition, CCU Online equips you for success to make a positive impact in the world.
            </p>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 row-start-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-1121" data-mp="183tsvfyrfayl">
            <form onSubmit={e => e.preventDefault()} id="ccu-lead-form" data-id="element-1122" data-mp="zaqact99c7x3">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1123" data-mp="1mzj7uu5j2ntt" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1124" data-mp="1rtdlpgygc8kz" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#0F3057] mb-4" data-id="element-1125" data-mp="215behwdmaw7e">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1126" data-mp="2ave3zmlj5s0z">
                <div className="bg-gradient-to-r from-[#D4A943] to-[#B8902F] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1127" data-mp="25x2trarbpfi8"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1128" data-mp="1e7rc81zeixtv">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1129" data-mp="1s5xzko6q5ffa">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* Why CCU Section */}
      <section className="bg-gray-50 py-16 border-b border-gray-200" data-id="element-1130" data-mp="1d37sr4b9p020">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1131" data-mp="1i3c82r0fvoqn">
          <div className="mb-12 text-center" data-id="element-1132" data-mp="1i1srezavfboc">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F3057]" data-id="element-1133" data-mp="1m578zuegk03w">
              Why CCU?
            </h2>
          </div>
          <div className="space-y-8 max-w-4xl mx-auto" data-id="element-1134" data-mp="1r2zbtjgn56mv">
            {/* Card 1 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1135" data-mp="1w1tr4bfegd28">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1136" data-mp="20vxzmz4bct5f">
                <ChurchIcon className="w-10 h-10" data-id="element-1137" data-mp="29ujnn9sk10dl" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1138" data-mp="29sfcq7w8n13t">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1139" data-mp="2euee5c8f3ny4">
                  Integration of Faith and Learning
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1140" data-mp="33n53a5t4mvt">
                  All courses are taught with a Christian worldview. This means
                  you will be equipped for success to make a positive impact in
                  the world.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1141" data-mp="lx0uz0uny34k">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1142" data-mp="hvp8h4735y0l">
                <RouteIcon className="w-10 h-10" data-id="element-1143" data-mp="8542uwszvt86" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1144" data-mp="84ue1cjors42">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1145" data-mp="15kqaa8leyaww">
                  Flexible Online Format
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1146" data-mp="10s6wpbmq7fs3">
                  CCU's unique online and course schedule format make it
                  possible for you to juggle life, work, and family all while
                  earning your degree.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1147" data-mp="1eza9ulfnba0h">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1148" data-mp="10yr63qi7qq44">
                <DollarSignIcon className="w-10 h-10" data-id="element-1149" data-mp="15wiuiajnfu3k" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1150" data-mp="s05mrt4x372a">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1151" data-mp="w066ntascagq">
                  Affordable
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1152" data-mp="2mclpijyi832e">
                  With competitive tuition rates, scholarships, and discount
                  opportunities, it's possible to earn your degree without
                  breaking the bank.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1153" data-mp="arcuwvcm1giw">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1154" data-mp="jpoieoi6jy74">
                <UsersIcon className="w-10 h-10" data-id="element-1155" data-mp="ydrqc3fs9jyk" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1156" data-mp="tgj6lyezl7v7">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1157" data-mp="ti42psgy8yv6">
                  Student Success
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1158" data-mp="12goc230mdw6q">
                  CCU sets you up for success with a dedicated support staff who are invested in making sure that you are successful from the first class, to graduation, and beyond.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1159" data-mp="2jd6dy3qegavi">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1160" data-mp="2nbvo7ng55cax">
                <MedalIcon className="w-10 h-10" data-id="element-1161" data-mp="2x3i785yq0fo8" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1162" data-mp="2x5vzy85xhfsm">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1163" data-mp="20ekbolj5cdq0">
                  Military Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1164" data-mp="24fe1jkg5g2lj">
                  Dedicated to serving the active and veteran military, CCU has a dedicated military enrollment team with expertise in advising those who are military-affiliated. Whether you're a veteran, active duty, or spouse of either, CCU's enrollment team will ensure you maximize your benefits as you achieve your academic goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Designed for Adult Students */}
      <section className="py-16 bg-white border-b border-gray-200" data-id="element-1165" data-mp="195h5fzt2j9pv">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1166" data-mp="198d2zn107wds">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F3057] mb-6" data-id="element-1167" data-mp="14dhaymvrp90o">
            Programs Designed for Adult Students
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-1168" data-mp="vecedm3rnpr5">
            CCU's online programs are specifically tailored for adult learners who need to balance their education with work and family commitments. With flexible scheduling and a supportive community, you can achieve your academic goals without putting your life on hold.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-1169" data-mp="gjfxyllszw2z">
            <div className="flex flex-col items-center" data-id="element-1170" data-mp="2ftxi7sx64jcg">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1171" data-mp="2auuoh4mn1zjs">
                <ShieldCheckIcon className="w-8 h-8 text-[#D4A943]" data-id="element-1172" data-mp="32l09ef12jbxb" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-1173" data-mp="2xn8inoc5tvup">
                Accredited Programs
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-1174" data-mp="3cb1xv7xc2wcr">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1175" data-mp="37cryel4kt9lc">
                <CheckCircle2Icon className="w-8 h-8 text-[#D4A943]" data-id="element-1176" data-mp="2jm4fl4t4gzk0" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-1177" data-mp="2eocp8f84llhx">
                100% Online
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-1178" data-mp="2tlvc1c18tomq">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1179" data-mp="2omabix6fd87w">
                <UsersIcon className="w-8 h-8 text-[#D4A943]" data-id="element-1180" data-mp="8ud0ocjn4yzr" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-1181" data-mp="3vrza9snk99q">
                Dedicated Support
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0F3057] py-16" data-id="element-1182" data-mp="ge454z32p5v5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1183" data-mp="gfyr8gq2z40t">
          <div className="w-12 h-1 bg-[#D4A943] mx-auto mb-6" data-id="element-1184" data-mp="2fnyt994hhdj"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-1185" data-mp="6fy38hh9nzqa">
            Interested in Learning More About CCU's Online{' '}
            <span className="text-[#D4A943]" data-id="element-1186" data-mp="u73iaufdunzj">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-1187" data-mp="z5eq6zm4aof4">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#0F3057] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-1188" data-mp="z4dgaygu87yz">
              Learn More Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-1189" data-mp="1d3d5q8c6cqw7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-1190" data-mp="1hwo2thr6ptax">
          <p data-id="element-1191" data-mp="1rqbqxlkfup2h">Certain degree programs may not be available in all states.</p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-1192" data-mp="1np8gjel5tl8n">
            Colorado Christian University is accredited by the Higher Learning
            Commission (hlcommission.org), an institutional accreditation agency
            recognized by the U.S. Department of Education.
          </p>
          <p className="text-gray-400" data-id="element-1193" data-mp="18sr40k18i0ki">
            © {new Date().getFullYear()} Colorado Christian University. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}